<template>
  <v-row class="patient-information-container">
    <v-col v-if="orderInfoPreview" cols="12">
      <v-row class="patient-information">
        <v-col cols="12">
          <h3 class="text-primary weight-600">Medical Information</h3>
        </v-col>

        <v-col cols="12">
          <FeedbackInputWrapper
            :field-id="'medication'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.medication"
            :error-messages="
              props.isEditable && !fieldNotEmpty('medication') && props.formButtonClicked
                ? 'Please enter medication.'
                : null
            "
            label="medication">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <div v-if="orderInfoPreview.medication?.length > 0">
                <v-text-field
                  v-model="orderInfoPreview.medication"
                  type="text"
                  placeholder="Medication"
                  label="Medication*"
                  class="lh--force-white-background"
                  :readonly="!props.isEditable"
                  :disabled="!props.isEditable"
                  :error-messages="errorMessages"
                  @input="onInput"
                  @change="
                    (e) => {
                      onChange(e);
                      emit('updateOrderInfoField', 'medication', orderInfoPreview.medication);
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </div>
              <div v-else>
                <MedicationSearchAutoComplete
                  :disabled="!props.isEditable"
                  :data="medicationSearchBoxTerms"
                  :required="true"
                  :allow-free-text-input="true"
                  :menu-props="{
                    contentClass: 'medication-autocomplete-menu',
                    maxHeight: 300,
                    position: 'absolute',
                    transition: false,
                    closeOnClick: true,
                    closeOnContentClick: true,
                    eager: true,
                  }"
                  @item-selected="
                    (event) => {
                      selectedMedication = event;
                      onInput(event);
                      emit('updateOrderInfoField', 'medication', orderInfoPreview.medication);
                    }
                  "
                  @focus="onFocus"
                  @blur="onBlur" />
              </div>
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="12" sm="6">
          <FeedbackInputWrapper
            :field-id="'frequency'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.frequency"
            label="frequency">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-combobox
                v-model="orderInfoPreview.frequency"
                :items="frequencyOptions"
                type="text"
                placeholder="Dosing Schedule [SIG]"
                label="Dosing Schedule [SIG]"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'frequency', orderInfoPreview.frequency);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="6" sm="4" md="3">
          <FeedbackInputWrapper
            :field-id="'dailyDosage'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.dailyDosage"
            label="dailyDosage">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.dailyDosage"
                type="text"
                placeholder="Dosage"
                label="Dosage"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'dailyDosage', orderInfoPreview.dailyDosage);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col v-if="isCoverMyMeds && !isCmmImport" cols="6" sm="4" md="3">
          <FeedbackInputWrapper
            :field-id="'nationalDrugCode'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.nationalDrugCode"
            :error-messages="
              props.isEditable && !nationalDrugCodeValid('nationalDrugCode') && props.formButtonClicked
                ? 'Please enter 11 digit NDC.'
                : null
            "
            label="nationalDrugCode">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.nationalDrugCode"
                type="text"
                placeholder="National Drug Code (NDC)"
                label="National Drug Code (NDC)*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    orderInfoPreview.nationalDrugCode = normalizeNdc(orderInfoPreview.nationalDrugCode);
                    onChange(e);
                    emit('updateOrderInfoField', 'nationalDrugCode', orderInfoPreview.nationalDrugCode);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col v-else cols="6" sm="4" md="3">
          <FeedbackInputWrapper
            :field-id="'nationalDrugCode'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.nationalDrugCode"
            label="nationalDrugCode">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.nationalDrugCode"
                type="text"
                placeholder="National Drug Code (NDC)"
                label="National Drug Code (NDC)"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    orderInfoPreview.nationalDrugCode = normalizeNdc(orderInfoPreview.nationalDrugCode);
                    onChange(e);
                    emit('updateOrderInfoField', 'nationalDrugCode', orderInfoPreview.nationalDrugCode);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col v-if="isCoverMyMeds" cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'packageSizeUnit'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.packageSizeUnit"
            :error-messages="
              props.isEditable && !packageSizeUnitValid('packageSizeUnit') && props.formButtonClicked
                ? 'Please select a valid package size unit.'
                : null
            "
            label="packageSizeUnit">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-select
                v-model="orderInfoPreview.packageSizeUnit"
                :items="requirementsStore.clientConfig?.intake?.packageSizeUnits || []"
                placeholder="Package Size Unit"
                label="Package Size Unit*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'packageSizeUnit', orderInfoPreview.packageSizeUnit);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col v-if="isCoverMyMeds" cols="12" sm="8">
          <FeedbackInputWrapper
            :field-id="'daw'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.daw"
            label="daw">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-select
                v-model="orderInfoPreview.daw"
                :items="dawOptions"
                placeholder="Should this request be reviewed for a brand only product (DAW-1)?"
                label="Should this request be reviewed for a brand only product (DAW-1)?"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'daw', orderInfoPreview.daw);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'quantityPer30Days'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.quantityPer30Days"
            :error-messages="
              props.isEditable && !numericalFieldValid('quantityPer30Days') && props.formButtonClicked
                ? 'Please enter a numerical value for quantity.'
                : null
            "
            label="quantityPer30Days">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.quantityPer30Days"
                type="text"
                placeholder="Quantity"
                label="Quantity*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'quantityPer30Days', orderInfoPreview.quantityPer30Days);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>

        <v-col cols="12" sm="6" md="4">
          <FeedbackInputWrapper
            :field-id="'expectedLengthOfTherapy'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.expectedLengthOfTherapy"
            :error-messages="
              props.isEditable && !numericalFieldValid('expectedLengthOfTherapy') && props.formButtonClicked
                ? 'Please enter days supply.'
                : null
            "
            label="expectedLengthOfTherapy">
            <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
              <v-text-field
                v-model="orderInfoPreview.expectedLengthOfTherapy"
                type="text"
                placeholder="Days Supply"
                label="Days Supply*"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                :error-messages="errorMessages"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'expectedLengthOfTherapy', orderInfoPreview.expectedLengthOfTherapy);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="6" sm="4" md="3">
          <FeedbackInputWrapper
            :field-id="'treatmentStartDate'"
            :order-key="orderInfoPreview.orderKey"
            :model-value="orderInfoPreview.treatmentStartDate"
            label="treatmentStartDate">
            <template #default="{ onInput, onChange, onFocus, onBlur }">
              <v-text-field
                v-model="orderInfoPreview.treatmentStartDate"
                type="date"
                placeholder="Treatment Start Date"
                label="Treatment Start Date"
                class="lh--force-white-background"
                :readonly="!props.isEditable"
                :disabled="!props.isEditable"
                @input="onInput"
                @change="
                  (e) => {
                    onChange(e);
                    emit('updateOrderInfoField', 'treatmentStartDate', orderInfoPreview.treatmentStartDate);
                  }
                "
                @focus="onFocus"
                @blur="onBlur" />
            </template>
          </FeedbackInputWrapper>
        </v-col>
        <v-col cols="2" sm="1" class="d-flex align-center justify-center">
          <v-btn
            v-if="props.isEditable"
            icon="mdi-delete"
            variant="text"
            color="error"
            @click="emit('removePrimaryMedication')" />
        </v-col>

        <v-col v-if="isCoverMyMeds && newMedicationSelected && props.allowChangeFormulary" cols="12" class="mt-4">
          <v-btn v-if="props.isEditable" variant="outlined" @click="emit('changeFormulary')">Change Formulary</v-btn>
        </v-col>

        <v-col
          v-if="
            (isFax || isManual) && orderInfoPreview.extraMedications && orderInfoPreview.extraMedications.length > 0
          "
          cols="12">
          <div
            v-for="(extraMed, index) in orderInfoPreview.extraMedications"
            :key="index"
            class="extra-medication-block">
            <v-divider class="mt-4 mb-4" />
            <v-row>
              <v-col cols="12">
                <div v-if="extraMed && 'medication' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedication' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.medication"
                    :error-messages="
                      props.isEditable && !extraMedFieldNotEmpty(extraMed, 'medication') && props.formButtonClicked
                        ? 'Please enter medication.'
                        : null
                    "
                    :label="'extraMedication' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <div v-if="extraMed.medication?.length > 0">
                        <v-text-field
                          v-model="extraMed.medication"
                          type="text"
                          placeholder="Medication"
                          label="Medication*"
                          class="lh--force-white-background"
                          :readonly="!props.isEditable"
                          :disabled="!props.isEditable"
                          :error-messages="errorMessages"
                          @input="onInput"
                          @change="
                            (e) => {
                              onChange(e);
                              emit('updateExtraMedicationField', index, 'medication', extraMed.medication);
                            }
                          "
                          @focus="onFocus"
                          @blur="onBlur" />
                      </div>
                      <div v-else>
                        <MedicationSearchAutoComplete
                          :disabled="!props.isEditable"
                          :data="medicationSearchBoxTerms"
                          :required="true"
                          :allow-free-text-input="true"
                          :menu-props="{
                            contentClass: 'medication-autocomplete-menu',
                            maxHeight: 300,
                            position: 'absolute',
                            transition: false,
                            closeOnClick: true,
                            closeOnContentClick: true,
                            eager: true,
                          }"
                          @item-selected="
                            (event) => {
                              handleSelectedExtraMedication(event, index);
                              onInput(event);
                              emit('updateExtraMedicationField', index, 'medication', extraMed.medication);
                            }
                          "
                          @focus="onFocus"
                          @blur="onBlur" />
                      </div>
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6">
                <div v-if="extraMed && 'frequency' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationFrequency' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.frequency"
                    :label="'extraMedicationFrequency' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.frequency"
                        type="text"
                        placeholder="Dosing Schedule"
                        label="Dosing Schedule"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'frequency', extraMed.frequency);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="6" sm="4" md="3">
                <div v-if="extraMed && 'dailyDosage' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationDailyDosage' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.dailyDosage"
                    :label="'extraMedicationDailyDosage' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.dailyDosage"
                        type="text"
                        placeholder="Dosage"
                        label="Dosage"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'dailyDosage', extraMed.dailyDosage);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="6" sm="4" md="3">
                <div v-if="extraMed && 'nationalDrugCode' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationNationalDrugCode' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.nationalDrugCode"
                    :label="'extraMedicationDailyDosage' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur }">
                      <v-text-field
                        v-model="extraMed.nationalDrugCode"
                        type="text"
                        placeholder="National Drug Code (NDC)"
                        label="National Drug Code (NDC)"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        @input="onInput"
                        @change="
                          (e) => {
                            extraMed.nationalDrugCode = normalizeNdc(extraMed.nationalDrugCode);
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'nationalDrugCode', extraMed.nationalDrugCode);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="12" sm="6" md="4">
                <div v-if="extraMed && 'quantityPer30Days' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationQuantityPer30Days' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.quantityPer30Days"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'quantityPer30Days') &&
                      !isNaN(extraMed.quantityPer30Days) &&
                      props.formButtonClicked
                        ? 'Please enter a numerical value for quantity.'
                        : null
                    "
                    :label="'extraMedicationQuantityPer30Days' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.quantityPer30Days"
                        type="text"
                        placeholder="Quantity"
                        label="Quantity*"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateExtraMedicationField', index, 'quantityPer30Days', extraMed.quantityPer30Days);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <div v-if="extraMed && 'expectedLengthOfTherapy' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationExpectedLengthOfTherapy' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.expectedLengthOfTherapy"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'expectedLengthOfTherapy') &&
                      !isNaN(extraMed.expectedLengthOfTherapy) &&
                      props.formButtonClicked
                        ? 'Please enter days supply.'
                        : null
                    "
                    :label="'extraMedicationExpectedLengthOfTherapy' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.expectedLengthOfTherapy"
                        type="text"
                        placeholder="Days Supply"
                        label="Days Supply*"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit(
                              'updateExtraMedicationField',
                              index,
                              'expectedLengthOfTherapy',
                              extraMed.expectedLengthOfTherapy
                            );
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="6" sm="4" md="3">
                <div v-if="extraMed && 'treatmentStartDate' in extraMed">
                  <FeedbackInputWrapper
                    :field-id="'extraMedicationTreatmentStartDate' + index"
                    :order-key="orderInfoPreview.orderKey"
                    :model-value="extraMed.treatmentStartDate"
                    :error-messages="
                      props.isEditable &&
                      !extraMedFieldNotEmpty(extraMed, 'treatmentStartDate') &&
                      !isNaN(extraMed.treatmentStartDate) &&
                      props.formButtonClicked
                        ? 'Please enter treatment start date.'
                        : null
                    "
                    :label="'extraMedicationTreatmentStartDate' + index">
                    <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                      <v-text-field
                        v-model="extraMed.treatmentStartDate"
                        type="date"
                        placeholder="Treatment Start Date"
                        label="Treatment Start Date"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit(
                              'updateExtraMedicationField',
                              index,
                              'treatmentStartDate',
                              extraMed.treatmentStartDate
                            );
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </template>
                  </FeedbackInputWrapper>
                </div>
              </v-col>
              <v-col cols="2" sm="1" class="d-flex align-center justify-center">
                <v-btn
                  v-if="props.isEditable"
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  @click="emit('removeExtraMedication', index)" />
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col v-if="isFax" cols="12">
          <v-btn
            v-if="props.isEditable"
            color="primary"
            variant="outlined"
            prepend-icon="mdi-plus"
            @click="emit('addExtraMedication')">
            Add Extra Medication
          </v-btn>
        </v-col>

        <!--
          Additional medications (LAM-4841): each becomes its OWN separate PA submission at
          Create Submission time, sharing this order's patient/provider/insurance/diagnosis/docs
          data — unlike extraMedications above, which lists multiple drugs on ONE submission.
          Distinct button color/label from "Add Extra Medication" so the two aren't confused.
        -->
        <v-col v-if="props.allowAdditionalMedications && additionalMedicationsList.length > 0" cols="12">
          <div
            v-for="(additionalMed, index) in additionalMedicationsList"
            :key="'additional-medication-' + index"
            class="additional-medication-block">
            <v-divider class="mt-4 mb-4" />
            <v-row>
              <v-col cols="12">
                <h4 class="text-primary weight-600">Additional Medication — Separate Submission</h4>
              </v-col>
              <v-col cols="12">
                <FeedbackInputWrapper
                  :field-id="'additionalMedication' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.medication"
                  :error-messages="
                    props.isEditable &&
                    !additionalMedFieldNotEmpty(additionalMed, 'medication') &&
                    props.formButtonClicked
                      ? 'Please enter medication.'
                      : null
                  "
                  :label="'additionalMedication' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                    <div v-if="additionalMed.medication?.length > 0">
                      <v-text-field
                        v-model="additionalMed.medication"
                        type="text"
                        placeholder="Medication"
                        label="Medication*"
                        class="lh--force-white-background"
                        :readonly="!props.isEditable"
                        :disabled="!props.isEditable"
                        :error-messages="errorMessages"
                        @input="onInput"
                        @change="
                          (e) => {
                            onChange(e);
                            emit('updateAdditionalMedicationField', index, 'medication', additionalMed.medication);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </div>
                    <div v-else>
                      <MedicationSearchAutoComplete
                        :disabled="!props.isEditable"
                        :data="medicationSearchBoxTerms"
                        :required="true"
                        :allow-free-text-input="true"
                        :menu-props="{
                          contentClass: 'medication-autocomplete-menu',
                          maxHeight: 300,
                          position: 'absolute',
                          transition: false,
                          closeOnClick: true,
                          closeOnContentClick: true,
                          eager: true,
                        }"
                        @item-selected="
                          (event) => {
                            handleSelectedAdditionalMedication(event, index);
                            onInput(event);
                            emit('updateAdditionalMedicationField', index, 'medication', additionalMed.medication);
                          }
                        "
                        @focus="onFocus"
                        @blur="onBlur" />
                    </div>
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="12" sm="6">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationFrequency' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.frequency"
                  :label="'additionalMedicationFrequency' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur }">
                    <v-combobox
                      v-model="additionalMed.frequency"
                      :items="frequencyOptions"
                      type="text"
                      placeholder="Dosing Schedule [SIG]"
                      label="Dosing Schedule [SIG]"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit('updateAdditionalMedicationField', index, 'frequency', additionalMed.frequency);
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="6" sm="4" md="3">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationDailyDosage' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.dailyDosage"
                  :label="'additionalMedicationDailyDosage' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur }">
                    <v-text-field
                      v-model="additionalMed.dailyDosage"
                      type="text"
                      placeholder="Dosage"
                      label="Dosage"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit('updateAdditionalMedicationField', index, 'dailyDosage', additionalMed.dailyDosage);
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col v-if="isCoverMyMeds && !isCmmImport" cols="6" sm="4" md="3">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationNationalDrugCode' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.nationalDrugCode"
                  :error-messages="
                    props.isEditable && !additionalMedNdcValid(additionalMed) && props.formButtonClicked
                      ? 'Please enter 11 digit NDC.'
                      : null
                  "
                  :label="'additionalMedicationNationalDrugCode' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                    <v-text-field
                      v-model="additionalMed.nationalDrugCode"
                      type="text"
                      placeholder="National Drug Code (NDC)"
                      label="National Drug Code (NDC)*"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      :error-messages="errorMessages"
                      @input="onInput"
                      @change="
                        (e) => {
                          additionalMed.nationalDrugCode = normalizeNdc(additionalMed.nationalDrugCode);
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'nationalDrugCode',
                            additionalMed.nationalDrugCode
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col v-else cols="6" sm="4" md="3">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationNationalDrugCode' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.nationalDrugCode"
                  :label="'additionalMedicationNationalDrugCode' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur }">
                    <v-text-field
                      v-model="additionalMed.nationalDrugCode"
                      type="text"
                      placeholder="National Drug Code (NDC)"
                      label="National Drug Code (NDC)"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      @input="onInput"
                      @change="
                        (e) => {
                          additionalMed.nationalDrugCode = normalizeNdc(additionalMed.nationalDrugCode);
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'nationalDrugCode',
                            additionalMed.nationalDrugCode
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col v-if="isCoverMyMeds" cols="12" sm="6" md="4">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationPackageSizeUnit' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.packageSizeUnit"
                  :error-messages="
                    props.isEditable && !additionalMedPackageSizeUnitValid(additionalMed) && props.formButtonClicked
                      ? 'Please select a valid package size unit.'
                      : null
                  "
                  :label="'additionalMedicationPackageSizeUnit' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                    <v-select
                      v-model="additionalMed.packageSizeUnit"
                      :items="requirementsStore.clientConfig?.intake?.packageSizeUnits || []"
                      placeholder="Package Size Unit"
                      label="Package Size Unit*"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      :error-messages="errorMessages"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'packageSizeUnit',
                            additionalMed.packageSizeUnit
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col v-if="isCoverMyMeds" cols="12" sm="8">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationDaw' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.daw"
                  :label="'additionalMedicationDaw' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur }">
                    <v-select
                      v-model="additionalMed.daw"
                      :items="dawOptions"
                      placeholder="Should this request be reviewed for a brand only product (DAW-1)?"
                      label="Should this request be reviewed for a brand only product (DAW-1)?"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit('updateAdditionalMedicationField', index, 'daw', additionalMed.daw);
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationQuantityPer30Days' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.quantityPer30Days"
                  :error-messages="
                    props.isEditable &&
                    !additionalMedNumericalFieldValid(additionalMed, 'quantityPer30Days') &&
                    props.formButtonClicked
                      ? 'Please enter a numerical value for quantity.'
                      : null
                  "
                  :label="'additionalMedicationQuantityPer30Days' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                    <v-text-field
                      v-model="additionalMed.quantityPer30Days"
                      type="text"
                      placeholder="Quantity"
                      label="Quantity*"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      :error-messages="errorMessages"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'quantityPer30Days',
                            additionalMed.quantityPer30Days
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="12" sm="6" md="4">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationExpectedLengthOfTherapy' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.expectedLengthOfTherapy"
                  :error-messages="
                    props.isEditable &&
                    !additionalMedNumericalFieldValid(additionalMed, 'expectedLengthOfTherapy') &&
                    props.formButtonClicked
                      ? 'Please enter days supply.'
                      : null
                  "
                  :label="'additionalMedicationExpectedLengthOfTherapy' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur, errorMessages }">
                    <v-text-field
                      v-model="additionalMed.expectedLengthOfTherapy"
                      type="text"
                      placeholder="Days Supply"
                      label="Days Supply*"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      :error-messages="errorMessages"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'expectedLengthOfTherapy',
                            additionalMed.expectedLengthOfTherapy
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="6" sm="4" md="3">
                <FeedbackInputWrapper
                  :field-id="'additionalMedicationTreatmentStartDate' + index"
                  :order-key="orderInfoPreview.orderKey"
                  :model-value="additionalMed.treatmentStartDate"
                  :label="'additionalMedicationTreatmentStartDate' + index">
                  <template #default="{ onInput, onChange, onFocus, onBlur }">
                    <v-text-field
                      v-model="additionalMed.treatmentStartDate"
                      type="date"
                      placeholder="Treatment Start Date"
                      label="Treatment Start Date"
                      class="lh--force-white-background"
                      :readonly="!props.isEditable"
                      :disabled="!props.isEditable"
                      @input="onInput"
                      @change="
                        (e) => {
                          onChange(e);
                          emit(
                            'updateAdditionalMedicationField',
                            index,
                            'treatmentStartDate',
                            additionalMed.treatmentStartDate
                          );
                        }
                      "
                      @focus="onFocus"
                      @blur="onBlur" />
                  </template>
                </FeedbackInputWrapper>
              </v-col>
              <v-col cols="2" sm="1" class="d-flex align-center justify-center">
                <v-btn
                  v-if="props.isEditable"
                  icon="mdi-delete"
                  variant="text"
                  color="error"
                  @click="emit('removeAdditionalMedication', index)" />
              </v-col>
            </v-row>
          </div>
        </v-col>
        <v-col v-if="props.allowAdditionalMedications" cols="12">
          <v-btn
            v-if="props.isEditable"
            color="accent"
            variant="outlined"
            prepend-icon="mdi-plus"
            :disabled="additionalMedicationsList.length >= MAX_ADDITIONAL_MEDICATIONS"
            @click="emit('addAdditionalMedication')">
            Add Another Medication for This Patient
          </v-btn>
          <div v-if="props.isEditable && additionalMedicationsList.length >= MAX_ADDITIONAL_MEDICATIONS" class="mt-2">
            <span class="text-caption text-medium-emphasis">
              Maximum of {{ MAX_ADDITIONAL_MEDICATIONS }} additional medications reached.
            </span>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { generateRandom4DigitNumber, isValidNdc, normalizeNdc } from "@/utils";
import { getMedicationDefaultsForName, applyMedicationDefaultsToTarget } from "@/utils/clientConfig";
import MedicationSearchAutoComplete from "@/components/MedicationSearchAutoComplete.vue";
import FeedbackInputWrapper from "@/components/FeedbackInputWrapper.vue";
import { useRequirementsStore } from "@/stores";
import { mainServices } from "@/services/mainServices";
import { MAX_ADDITIONAL_MEDICATIONS } from "@/composables/useOrderInfoUpdates";
const requirementsStore = useRequirementsStore();

// Client-configured frequency codes (ClientCompanyData category=order_frequency_multiplier, e.g.
// Brooks' "TID"/"3xd"). Bound to a v-combobox so a value picked here always exactly matches what
// refresh_medication_derived_fields looks up server-side to compute daily_caloric_requirement —
// free typing still works (empty for clients with none configured behaves like a plain text field).
const frequencyOptions = ref([]);

async function loadFrequencyOptions() {
  const clientCompanyId = requirementsStore.clientConfig?.id;
  if (!clientCompanyId) return;
  try {
    const response = await mainServices.getClientCompanyData(clientCompanyId, "order_frequency_multiplier");
    frequencyOptions.value = (response.results || response || []).map((row) => row.textValue);
  } catch (error) {
    frequencyOptions.value = [];
  }
}

onMounted(async () => {
  // Load client config for current user if not already loaded
  if (!requirementsStore.clientConfig) {
    await requirementsStore.refreshCurrentUserClientConfig();
  }
  await loadFrequencyOptions();
});

const selectedMedication = ref(null);
const newMedicationSelected = ref(false);
const dawOptions = ["Yes", "No"];

const emit = defineEmits([
  "updateOrderInfoField",
  "updateExtraMedicationField",
  "addExtraMedication",
  "removeExtraMedication",
  "removePrimaryMedication",
  "changeFormulary",
  "updateAdditionalMedicationField",
  "addAdditionalMedication",
  "removeAdditionalMedication",
]);

const props = defineProps({
  orderInfo: {
    type: Object,
    required: true,
  },
  isEditable: {
    type: Boolean,
    default() {
      return false;
    },
  },
  formButtonClicked: {
    type: Boolean,
    default() {
      return false;
    },
  },
  medicationSearchBox: {
    type: Object,
    default() {
      return {};
    },
  },
  // Gates the "add another medication for this patient" section (LAM-4841) — only Create
  // Submission passes this; AllFormStepsView's existing-order edit flow never does.
  allowAdditionalMedications: {
    type: Boolean,
    default() {
      return false;
    },
  },
  // Gates the CoverMyMeds "Change Formulary" action — it calls changeOrderFormulary(orderKey),
  // so it only makes sense for an existing order. Create Submission never passes this.
  allowChangeFormulary: {
    type: Boolean,
    default() {
      return false;
    },
  },
});

const orderInfoPreview = ref(props.orderInfo);

const medicationSearchBoxTerms = computed(() => {
  return {
    label: "Medication",
    searchId: generateRandom4DigitNumber(),
  };
});

const isFax = computed(() => orderInfoPreview.value.submissionMethod === "eFax");
const isManual = computed(() => orderInfoPreview.value.submissionMethod === "Manual");
const isCoverMyMeds = computed(() => orderInfoPreview.value.submissionMethod === "CoverMyMeds");
const isCmmImport = computed(() => orderInfoPreview.value.orderSubmission?.isCmmImport);

function fieldNotEmpty(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "";
}

function numericalFieldValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && !isNaN(value);
}

function nationalDrugCodeValid(field) {
  const value = orderInfoPreview.value[field];
  return value !== null && value.trim() !== "" && isValidNdc(value);
}

function packageSizeUnitValid(field) {
  const value = orderInfoPreview.value[field];
  return (
    value !== null && value.trim() !== "" && requirementsStore.clientConfig?.intake?.packageSizeUnits.includes(value)
  );
}

function extraMedFieldNotEmpty(extraMed, field) {
  if (!extraMed[field]) return false;
  return extraMed[field].trim() !== "";
}

const additionalMedicationsList = computed(() => orderInfoPreview.value.additionalMedications || []);

function additionalMedFieldNotEmpty(additionalMed, field) {
  if (!additionalMed[field]) return false;
  return additionalMed[field].trim() !== "";
}

function additionalMedNumericalFieldValid(additionalMed, field) {
  return additionalMedFieldNotEmpty(additionalMed, field) && !isNaN(additionalMed[field]);
}

function additionalMedNdcValid(additionalMed) {
  const value = additionalMed.nationalDrugCode;
  return additionalMedFieldNotEmpty(additionalMed, "nationalDrugCode") && isValidNdc(value);
}

function additionalMedPackageSizeUnitValid(additionalMed) {
  return (
    additionalMedFieldNotEmpty(additionalMed, "packageSizeUnit") &&
    requirementsStore.clientConfig?.intake?.packageSizeUnits.includes(additionalMed.packageSizeUnit)
  );
}

function getMedicationDefaultsForSelection(selectedItem) {
  return getMedicationDefaultsForName(
    selectedItem?.value?.name ?? "",
    requirementsStore.clientConfig?.priorAuth?.medicationDefaults
  );
}

watch(selectedMedication, (newSelectedMedication) => {
  if (newSelectedMedication) {
    orderInfoPreview.value.medication = newSelectedMedication.value.medication
      ? newSelectedMedication.value.medication
      : newSelectedMedication.value.label;
    orderInfoPreview.value.packageSizeUnit = newSelectedMedication.value.packageSizeUnit
      ? newSelectedMedication.value.packageSizeUnit
      : "";
    orderInfoPreview.value.nationalDrugCode = newSelectedMedication.value.nationalDrugCode
      ? newSelectedMedication.value.nationalDrugCode
      : "";
    const defaults = getMedicationDefaultsForSelection(newSelectedMedication);
    if (defaults) {
      applyMedicationDefaultsToTarget(defaults, orderInfoPreview.value, (field, value) =>
        emit("updateOrderInfoField", field, value)
      );
    }
    newMedicationSelected.value = true;
  }
});

function handleSelectedExtraMedication(newSelectedMedication, index) {
  if (newSelectedMedication) {
    const extraMed = orderInfoPreview.value.extraMedications[index];
    extraMed.medication = newSelectedMedication.value.medication
      ? newSelectedMedication.value.medication
      : newSelectedMedication.value.label;
    extraMed.packageSizeUnit = newSelectedMedication.value.packageSizeUnit
      ? newSelectedMedication.value.packageSizeUnit
      : "";
    extraMed.nationalDrugCode = newSelectedMedication.value.nationalDrugCode
      ? newSelectedMedication.value.nationalDrugCode
      : "";
    const defaults = getMedicationDefaultsForSelection(newSelectedMedication);
    if (defaults) {
      applyMedicationDefaultsToTarget(defaults, extraMed, (field, value) =>
        emit("updateExtraMedicationField", index, field, value)
      );
    }
  }
}

function handleSelectedAdditionalMedication(newSelectedMedication, index) {
  if (newSelectedMedication) {
    const additionalMed = orderInfoPreview.value.additionalMedications[index];
    additionalMed.medication = newSelectedMedication.value.medication
      ? newSelectedMedication.value.medication
      : newSelectedMedication.value.label;
    additionalMed.packageSizeUnit = newSelectedMedication.value.packageSizeUnit
      ? newSelectedMedication.value.packageSizeUnit
      : "";
    additionalMed.nationalDrugCode = newSelectedMedication.value.nationalDrugCode
      ? newSelectedMedication.value.nationalDrugCode
      : "";
    const defaults = getMedicationDefaultsForSelection(newSelectedMedication);
    if (defaults) {
      applyMedicationDefaultsToTarget(defaults, additionalMed, (field, value) =>
        emit("updateAdditionalMedicationField", index, field, value)
      );
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../styles/pages/_patient-information-form.scss";
.medication-autocomplete-menu {
  position: absolute !important;
  z-index: 1000;
  transform: none !important;
}
</style>
